import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { ApiError, makeClient } from '../api/client'

// Membership mirrors backend/internal/api/auth_handlers.go membershipPayload.
// A user carries the full list; `active` is the one currently projected
// onto the JWT and the tenant-scoped API queries.
export type Membership = {
  tenant_id: string
  tenant_slug: string
  tenant_name?: string
  role: string
  is_platform: boolean
}

export type AuthUser = {
  id: string
  email: string
  display_name: string
  tenant_id: string
  tenant_slug: string
  roles: string[]
  memberships: Membership[]
  active: Membership
}

// LoginResult is what the login helpers return so the caller (LoginPage)
// can decide whether to route the user straight in or show the workspace
// picker step. `needs_pick` is a UI concern only — the token issued IS
// usable; a user with 3 memberships already has one active. The picker
// lets them re-pick via /auth/switch before landing.
export type LoginResult = {
  user: AuthUser
  needs_pick: boolean
}

type LoginResponse = {
  access_token: string
  refresh_token: string
  token_type: 'Bearer'
  expires_in: number
  user: AuthUser
}

type AuthState = {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
  loading: boolean
  error: ApiError | null
  login: (email: string, password: string) => Promise<LoginResult>
  requestOtp: (email: string) => Promise<void>
  verifyOtp: (email: string, code: string) => Promise<LoginResult>
  switchMembership: (tenantID: string, role: string) => Promise<AuthUser>
  logout: () => void
}

const Ctx = createContext<AuthState | undefined>(undefined)

const ACCESS_KEY = 'fieldlens.access'
const REFRESH_KEY = 'fieldlens.refresh'
const USER_KEY = 'fieldlens.user'

function readStored(): {
  access: string | null
  refresh: string | null
  user: AuthUser | null
} {
  try {
    const access = sessionStorage.getItem(ACCESS_KEY)
    const refresh = sessionStorage.getItem(REFRESH_KEY)
    const userRaw = sessionStorage.getItem(USER_KEY)
    const user = userRaw ? (JSON.parse(userRaw) as AuthUser) : null
    return { access, refresh, user }
  } catch {
    return { access: null, refresh: null, user: null }
  }
}

function commitSession(data: LoginResponse) {
  sessionStorage.setItem(ACCESS_KEY, data.access_token)
  sessionStorage.setItem(REFRESH_KEY, data.refresh_token)
  sessionStorage.setItem(USER_KEY, JSON.stringify(data.user))
}

function pickResult(data: LoginResponse): LoginResult {
  return {
    user: data.user,
    // If the user holds more than one membership AND hasn't visited
    // /auth/switch yet this session, the LoginPage shows the picker.
    // The token IS still valid — picker just gives them a chance to
    // choose a workspace other than the default the backend selected.
    needs_pick: (data.user.memberships?.length ?? 0) > 1,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initial = readStored()
  const [accessToken, setAccessToken] = useState<string | null>(initial.access)
  const [refreshToken, setRefreshToken] = useState<string | null>(initial.refresh)
  const [user, setUser] = useState<AuthUser | null>(initial.user)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)

  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem(ACCESS_KEY)
      sessionStorage.removeItem(REFRESH_KEY)
      sessionStorage.removeItem(USER_KEY)
    } catch {
      // sessionStorage can throw in private mode / with SecurityError —
      // clearing state below still gets us to /login.
    }
    setAccessToken(null)
    setRefreshToken(null)
    setUser(null)
    setError(null)
  }, [])

  // The client reads the CURRENT token via the getter closure — so a
  // switchMembership that updates accessToken feeds through immediately.
  //
  // onUnauthorized: any authed request that comes back 401 (token expired
  // or revoked mid-session) triggers logout, which clears the session and
  // — because <RequireAuth> depends on accessToken — bounces the user to
  // /login on the next render.
  const client = useMemo(() => makeClient(() => accessToken, logout), [accessToken, logout])

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      setLoading(true)
      setError(null)
      try {
        const data = await client.call<LoginResponse>('/api/v1/auth/login', {
          method: 'POST',
          body: { email, password },
          authed: false,
        })
        commitSession(data)
        setAccessToken(data.access_token)
        setRefreshToken(data.refresh_token)
        setUser(data.user)
        return pickResult(data)
      } catch (e) {
        if (e instanceof ApiError) setError(e)
        else setError(new ApiError(0, 'network', e instanceof Error ? e.message : String(e)))
        throw e
      } finally {
        setLoading(false)
      }
    },
    [client],
  )

  const requestOtp = useCallback(
    async (email: string): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        await client.call<{ sent: true }>('/api/v1/auth/request-otp', {
          method: 'POST',
          body: { email },
          authed: false,
        })
      } catch (e) {
        if (e instanceof ApiError) setError(e)
        else setError(new ApiError(0, 'network', e instanceof Error ? e.message : String(e)))
        throw e
      } finally {
        setLoading(false)
      }
    },
    [client],
  )

  const verifyOtp = useCallback(
    async (email: string, code: string): Promise<LoginResult> => {
      setLoading(true)
      setError(null)
      try {
        const data = await client.call<LoginResponse>('/api/v1/auth/verify-otp', {
          method: 'POST',
          body: { email, code },
          authed: false,
        })
        commitSession(data)
        setAccessToken(data.access_token)
        setRefreshToken(data.refresh_token)
        setUser(data.user)
        return pickResult(data)
      } catch (e) {
        if (e instanceof ApiError) setError(e)
        else setError(new ApiError(0, 'network', e instanceof Error ? e.message : String(e)))
        throw e
      } finally {
        setLoading(false)
      }
    },
    [client],
  )

  const switchMembership = useCallback(
    async (tenantID: string, role: string): Promise<AuthUser> => {
      setLoading(true)
      setError(null)
      try {
        const data = await client.call<LoginResponse>('/api/v1/auth/switch', {
          method: 'POST',
          body: { tenant_id: tenantID, role },
        })
        commitSession(data)
        setAccessToken(data.access_token)
        setRefreshToken(data.refresh_token)
        setUser(data.user)
        return data.user
      } catch (e) {
        if (e instanceof ApiError) setError(e)
        else setError(new ApiError(0, 'network', e instanceof Error ? e.message : String(e)))
        throw e
      } finally {
        setLoading(false)
      }
    },
    [client],
  )

  // Re-hydrate the stored user against /me on mount. Ensures memberships
  // + active are current even if the sessionStorage snapshot is stale
  // (schema evolution, admin revoked a membership between tabs, etc.).
  useEffect(() => {
    if (!accessToken) return
    const abort = new AbortController()
    client
      .call<AuthUser>('/api/v1/me', { signal: abort.signal })
      .then((u) => {
        setUser(u)
        sessionStorage.setItem(USER_KEY, JSON.stringify(u))
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) logout()
      })
    return () => abort.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      accessToken,
      refreshToken,
      loading,
      error,
      login,
      requestOtp,
      verifyOtp,
      switchMembership,
      logout,
    }),
    [user, accessToken, refreshToken, loading, error, login, requestOtp, verifyOtp, switchMembership, logout],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>')
  return v
}
